/**
 * Utilidades de formateo para el sistema CBR
 */

/**
 * Formatea una lista de repertorios agrupando números consecutivos en rangos legibles.
 * Ejemplo: [800, 801, ..., 900] => "800 al 900"
 * Ejemplo: [100, 101, 105, 110, 111, 112] => "100 al 101, 105, 110 al 112"
 * 
 * @param {Array} repertorios Array de objetos { numero_repertorio } o números
 * @returns {string} Cadena formateada como rango o lista
 */
export function formatRepertoriosRange(repertorios) {
  if (!repertorios || !Array.isArray(repertorios) || repertorios.length === 0) {
    return '';
  }

  // Extraer valores ya sea que vengan como objetos { numero_repertorio } o números/strings
  const rawValues = repertorios
    .map(r => (typeof r === 'object' && r !== null ? r.numero_repertorio : r))
    .filter(val => val !== null && val !== undefined && val !== '');

  if (rawValues.length === 0) return '';

  // Verificar si todos son valores numéricos enteros
  const isNumericList = rawValues.every(val => !isNaN(Number(val)) && Number.isInteger(Number(val)));

  if (!isNumericList) {
    return rawValues.join(', ');
  }

  // Ordenar numéricamente de menor a mayor y descartar duplicados
  const sorted = Array.from(new Set(rawValues.map(n => Number(n)))).sort((a, b) => a - b);

  const ranges = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    if (current === rangeEnd + 1) {
      rangeEnd = current;
    } else {
      ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart} al ${rangeEnd}`);
      rangeStart = current;
      rangeEnd = current;
    }
  }
  ranges.push(rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart} al ${rangeEnd}`);

  return ranges.join(', ');
}

/**
 * Obtiene la lista de comprobantes de pago aceptados para un trámite.
 * Soporta múltiples comprobantes (ej. cotización inicial + cotizaciones de reajustes pagados).
 * Deduplica por URL de comprobante.
 *
 * @param {Object} item Trámite
 * @param {Object|null} detail Detalle extendido si existe
 * @returns {Array} Lista de cotizaciones con comprobantes aceptados
 */
export function getComprobantesAceptados(item, detail = null) {
  if (!item && !detail) return [];

  const allCotizaciones = [
    ...(detail?.cotizaciones || item?.cotizaciones || []),
    detail?.cotizacion,
    item?.cotizacion
  ].filter(Boolean);

  const rawEstado = (item?.estado?.nombre || detail?.estado?.nombre || '').toLowerCase();
  const isPaidOrActive = rawEstado.includes('pagad') || 
                         rawEstado.includes('proces') || 
                         rawEstado.includes('complet') ||
                         rawEstado.includes('recibid');

  const seen = new Set();
  const result = [];

  allCotizaciones.forEach(c => {
    const url = c.comprobante_url || (c.comprobante_pago_path ? `/api/v1/tramites/${item?.id || detail?.id}/comprobante?cotizacion_id=${c.id}` : null);
    if (!url) return;

    // Descartar comprobantes de cotizaciones expresamente rechazadas o devueltas
    if (c.estado === 'rechazada' || c.estado === 'devuelto') return;

    const key = c.id ? `id-${c.id}` : url;
    if (seen.has(key)) return;

    const esValido = c.estado === 'pagada' || isPaidOrActive || !!c.comprobante_pago_path || !!c.comprobante_url;
    if (esValido) {
      seen.add(key);
      result.push({
        ...c,
        comprobante_url: url
      });
    }
  });

  return result;
}
